import { useAuth as authStore } from "@/stores/use-auth-store";
import ky, { Hooks, KyRequest, NormalizedOptions } from "ky";
import Cookies from "js-cookie";
import { merge } from "lodash";
import { API_CONFIG, getBaseUrl } from "@/lib/api-config";

const API_TIMEOUT_MS = API_CONFIG.TIMEOUT;

// Get Clerk token - this will be set by the auth hook
const getAuthToken = (): string | null => {
  return authStore.getState().token;
};

const getUserId = (): string | null => {
  return authStore.getState().userId;
};

// Custom hook for adding authorization header
const setupHooks = (): Hooks => {
  return {
    beforeRequest: [
      // Setup default headers
      async (request: Request, _options: NormalizedOptions) => {
        request.headers.set("Content-Type", "application/json");
        const clientId = Cookies.get("clientId");
        if (clientId) request.headers.set("clientId", clientId);
      },

      // Setup auth headers
      async (request: Request, _options: NormalizedOptions) => {
        const token = getAuthToken();
        const userId = getUserId();

        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
        if (userId) {
          request.headers.set("clientUserId", userId);
        }

        return request;
      },
    ],
    afterResponse: [
      async (request: KyRequest, options: NormalizedOptions, response) => {
        if (response.status === 401 && request.headers.get("x-try-count")) {
          // Redirect to sign-in on unauthorized
          if (typeof window !== "undefined") {
            window.location.href = "/sign-in";
          }
        }

        return response;
      },
    ],
    beforeError: [
      async (error: any) => {
        try {
          const err = await error.response.json();
          return merge(error, err);
        } catch {
          return error;
        }
      },
    ],
    beforeRetry: [
      async ({ request, options: _options, error, retryCount }) => {
        if ((error as any).response?.status === 401) {
          request.headers.set("x-try-count", retryCount.toString());
          // Try to refresh token from auth store
          const token = getAuthToken();
          if (token) {
            request.headers.set("Authorization", `Bearer ${token}`);
          }
        }
      },
    ],
  };
};

let clientInstance: ReturnType<typeof ky.create> | null = null;

function getClient() {
  if (!clientInstance) {
    clientInstance = ky.create({
      hooks: setupHooks(),
      prefixUrl: getBaseUrl(),
      retry: {
        limit: 1,
        statusCodes: [401],
        methods: ["get", "put", "head", "delete", "options", "trace", "post", "patch", "connect"]
      },
      timeout: API_TIMEOUT_MS
    });
  }
  return clientInstance;
}

export default new Proxy({} as ReturnType<typeof ky.create>, {
  get(_, prop) {
    return getClient()[prop as keyof ReturnType<typeof ky.create>];
  }
});
